import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { ApiService } from './api.service';
import { environment } from 'src/environments/environment';
import { createRemoteJWKSet, jwtVerify } from 'jose';

const HOSTNAME_O3 = environment.api.o3;

export interface ApplicationTokenResponse {
  token: string;
  expiresIn: number;
  audience: string;
  expiresAt: string;
}

export interface IApplicationToken {
  iss: string;
  sub: string;
  aud: string;
  iat: number;
  nbf: number;
  exp: number;
  jti: string;
  loginid: string;
  tenantid: string;
  roles: string;
}

@Injectable({
  providedIn: 'root',
})
export class ApplicationTokenService {
  constructor(private apiService: ApiService) {}

  mintToken(applicationId: string): Observable<ApplicationTokenResponse> {
    return this.apiService.post<
      { applicationId: string },
      ApplicationTokenResponse
    >('auth/application-token', { applicationId }, HOSTNAME_O3);
  }

  decodeToken(token: string): IApplicationToken {
    const parts = token.split('.');
    const payload = JSON.parse(atob(parts[1]));
    return payload;
  }

  verifyToken(token: string, expectedAudience: string): Observable<boolean> {
    const apiUrl = new URL(HOSTNAME_O3);
    const jwksUrl = new URL('/.well-known/jwks.json', apiUrl.origin);
    const JWKS = createRemoteJWKSet(jwksUrl);

    return from(
      jwtVerify(token, JWKS, {
        issuer: apiUrl.origin,
        audience: expectedAudience,
      })
        .then(() => true)
        .catch((error) => {
          console.error('Token verification failed:', error);
          return false;
        })
    );
  }

  authenticateAndRedirect(
    applicationId: string,
    targetUrl: string
  ): Observable<void> {
    return new Observable<void>((observer) => {
      console.log('Starting SSO authentication flow', {
        applicationId,
        targetUrl,
      });

      this.mintToken(applicationId).subscribe({
        next: (response) => {
          console.log('Application token minted successfully', {
            audience: response.audience,
            expiresAt: response.expiresAt,
            expiresIn: response.expiresIn,
          });

          console.log('Making POST request to SSO endpoint', {
            url: targetUrl,
            method: 'POST',
            token: response.token,
            applicationId: applicationId,
            targetUrl: targetUrl,
          });

          fetch(targetUrl, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${response.token}`,
            },
            credentials: 'include',
          })
            .then((fetchResponse) => {
              console.log('Received response from SSO endpoint', {
                status: fetchResponse.status,
                statusText: fetchResponse.statusText,
                redirected: fetchResponse.redirected,
                finalUrl: fetchResponse.url,
              });

              if (fetchResponse.redirected) {
                console.log('Opening redirect in new tab', fetchResponse.url);
                window.open(fetchResponse.url, '_blank', 'noopener,noreferrer');
                observer.next();
                observer.complete();
              } else if (fetchResponse.status === 401) {
                throw new Error('Invalid authentication token');
              } else if (fetchResponse.status === 400) {
                throw new Error('Tenant ID not found');
              } else if (fetchResponse.status >= 500) {
                throw new Error('Server error occurred');
              } else {
                throw new Error(
                  `Expected redirect but got status ${fetchResponse.status}`
                );
              }
            })
            .catch((error) => {
              console.error('SSO authentication error:', error);
              observer.error(error);
            });
        },
        error: (error) => {
          console.error('Failed to mint application token:', error);
          observer.error(error);
        },
      });
    });
  }
}
